// src/components/ui/DatePicker/DateRangePicker.tsx
import React, { useState, useEffect } from 'react';
import { Flex } from '@/components/ui/Layout';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from 'react-i18next';

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onChange: (range: { startDate: Date; endDate: Date }) => void;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onChange,
  minDate,
  maxDate,
  className = ''
}) => {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const [endDate, setEndDate] = useState<Date>(initialEndDate);
  
  // Обновляем состояние компонента при изменении пропсов
  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // Форматирование даты для отображения в input
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0]; // формат "YYYY-MM-DD"
  };

  // Обработчик изменения начальной даты
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = new Date(e.target.value);
    
    // Проверяем минимальную дату
    if (minDate && newStartDate < minDate) {
      return;
    }
    
    // Если новая начальная дата после конечной, устанавливаем конечную равной начальной
    const newEndDate = newStartDate > endDate ? newStartDate : endDate;
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onChange({ startDate: newStartDate, endDate: newEndDate });
  };

  // Обработчик изменения конечной даты
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = new Date(e.target.value);
    
    // Проверяем максимальную дату
    if (maxDate && newEndDate > maxDate) {
      return;
    }
    
    // Если новая конечная дата до начальной, устанавливаем начальную равной конечной
    const newStartDate = newEndDate < startDate ? newEndDate : startDate;
    
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    onChange({ startDate: newStartDate, endDate: newEndDate });
  };

  return (
    <Flex 
      direction={isMobile ? "column" : "row"} 
      gap="md" 
      className={className}
    >
      <div className={isMobile ? "w-full" : "flex-1"}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('date_picker.start_date', 'Начальная дата')}
        </label>
        <input
          type="date"
          value={formatDateForInput(startDate)}
          onChange={handleStartDateChange}
          min={minDate ? formatDateForInput(minDate) : undefined}
          max={maxDate ? formatDateForInput(maxDate) : undefined}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>
      
      <div className={isMobile ? "w-full" : "flex-1"}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('date_picker.end_date', 'Конечная дата')}
        </label>
        <input
          type="date"
          value={formatDateForInput(endDate)}
          onChange={handleEndDateChange}
          min={formatDateForInput(startDate)}
          max={maxDate ? formatDateForInput(maxDate) : undefined}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
        />
      </div>
    </Flex>
  );
};

export default DateRangePicker;